import type { TennisMatchFormat } from '../../data/tennisMatchFormats';
import type { MatchPoint, RallyBucket } from '../../types';
import { SPORT_STAT_KEYS } from '../../data/sportStats';
import { reduce, isGameWon, type Side } from './scoring';

/**
 * Projects the match_points log into per-player Tennis stat totals (sc-339 /
 * Tennis Phase 5). Pure and synchronous — the point log is the single source
 * of truth, this is just another projection of it (same philosophy as the
 * scoring engine itself). The caller resolves player_id -> player_card_id and
 * writes the result to `sport_stats`.
 *
 * Doubles attribution — what's reliably knowable from the point log vs. not:
 * - Serve-side stats (ServePts_*, Aces, DoubleFaults, SvcGames, SvcGamesHeld):
 *   `server_player_id` is recorded on every point we served, so these are
 *   always exactly attributable.
 * - Return-side stats (RetPts/RetGames and their "Won" counterparts,
 *   BreakPts*): attributed to whichever of our two players is NOT the
 *   recorded `net_player_id` for that point — the same "net and back are
 *   complementary" model the scoring screen itself uses to derive who's
 *   serving (see useTennisScoringMatch's note). `net_player_id` is captured
 *   on every doubles point regardless of who's serving, so this is reliable.
 * - Winner/Unforced/Forced credit: only attributed to an individual when the
 *   scorer explicitly tagged `winning_player_id` (doubles + we won the
 *   point). There's no equivalent tag for "who made the error" today, so a
 *   doubles error point isn't attributed to either player individually —
 *   it still isn't lost, it just doesn't appear in an individual Tennis
 *   stat card until that tagging exists.
 * - Net play (NetPts/NetPtsWon) and rally-length buckets: `net_approach` and
 *   `rally_bucket` are point-level Pro-tier tags, not side-specific. NetPts
 *   is interpreted as "our net player's point" (attributed via
 *   `net_player_id`); rally-bucket counts are inherently about how the point
 *   unfolded as a team, so they're written identically to both doubles
 *   partners, the same way a team outcome (SetsWon, GamesWon, MatchWon) is.
 */

const RALLY_KEY: Record<RallyBucket, string> = {
  '1-3': 'Rally_1to3',
  '4-6': 'Rally_4to6',
  '7-9': 'Rally_7to9',
  '10+': 'Rally_10Plus',
};

const ALL_TENNIS_KEYS = SPORT_STAT_KEYS.Tennis.map((k) => k.key);

export interface TennisStatsProjectionInput {
  format: TennisMatchFormat;
  /** The full point log for the match, any order (sorted by seq internally). */
  points: MatchPoint[];
  isDoubles: boolean;
  /** Our participant player_ids: 1 entry for singles, 2 for doubles (slot order doesn't matter here). */
  participantPlayerIds: string[];
}

/** playerId -> statKey -> total value. Every declared Tennis key is present for every participant (0 if not applicable). */
export type TennisStatsProjection = Record<string, Record<string, number>>;

export function projectTennisMatchStats({
  format,
  points: rawPoints,
  isDoubles,
  participantPlayerIds,
}: TennisStatsProjectionInput): TennisStatsProjection {
  const points = [...rawPoints].sort((a, b) => a.seq - b.seq);
  const playerIds = participantPlayerIds.filter(Boolean);

  const stats: TennisStatsProjection = {};
  for (const id of playerIds) {
    stats[id] = Object.fromEntries(ALL_TENNIS_KEYS.map((k) => [k, 0]));
  }

  const add = (playerId: string | null | undefined, key: string, amount = 1) => {
    if (!playerId || !stats[playerId]) return;
    stats[playerId][key] += amount;
  };

  const backPlayerId = (netId: string | null | undefined): string | null => {
    if (!isDoubles) return playerIds[0] ?? null;
    return playerIds.find((id) => id !== netId) ?? null;
  };

  // One full replay for match-level truth: sets, games, tiebreaks, and whether
  // a trailing in-progress game actually concluded. O(n) — cheap even for a
  // long match, and far simpler than re-deriving any of this point-by-point.
  const engineState = reduce(format, points.map((p) => ({ winner: p.won_by })), { isDoubles });

  // ---- Per-point pass: serve stats, return points, winner/error credit, net play, rally buckets, break points ----
  let gameKey: string | null = null;
  let gameTally = { ours: 0, theirs: 0 };

  points.forEach((p) => {
    // Serve-side: always attributable via server_player_id when we serve.
    if (p.server === 'us' && p.serve_outcome) {
      add(p.server_player_id, 'ServePts_1st');
      if (p.serve_outcome === '1st_in') {
        add(p.server_player_id, 'ServePts_1stIn');
        if (p.won_by === 'us') add(p.server_player_id, 'ServePts_1stWon');
      } else {
        add(p.server_player_id, 'ServePts_2nd');
        if (p.serve_outcome === '2nd_in' && p.won_by === 'us') add(p.server_player_id, 'ServePts_2ndWon');
        if (p.serve_outcome === 'double_fault') add(p.server_player_id, 'DoubleFaults');
      }
    }
    if (p.end_reason === 'ace' && p.server === 'us' && p.won_by === 'us') {
      add(p.server_player_id, 'Aces');
    }

    // Return-side: attributed to whichever of our players isn't at net.
    if (p.server === 'them') {
      const returner = backPlayerId(p.net_player_id);
      add(returner, 'RetPts');
      if (p.won_by === 'us') add(returner, 'RetPtsWon');
    }

    // Winner/error credit: explicit tag for doubles, automatic for singles.
    const winCreditPlayer = !isDoubles ? playerIds[0] : p.won_by === 'us' ? p.winning_player_id : null;
    if (p.end_reason === 'winner' && p.won_by === 'us') add(winCreditPlayer, 'Winners');

    const errorCreditPlayer = !isDoubles ? playerIds[0] : null; // no "who erred" tag exists for doubles yet
    if (p.end_reason === 'unforced' && p.won_by === 'them') {
      add(errorCreditPlayer, 'UnforcedErrors');
      if (p.error_spot === 'net') add(errorCreditPlayer, 'UnforcedErrors_Net');
      if (p.error_spot === 'long') add(errorCreditPlayer, 'UnforcedErrors_Long');
      if (p.error_spot === 'wide') add(errorCreditPlayer, 'UnforcedErrors_Wide');
    }
    if (p.end_reason === 'forced' && p.won_by === 'them') add(errorCreditPlayer, 'ForcedErrors');

    // Net play — attributed to our net player for that point.
    if (p.net_approach) {
      const netPlayer = isDoubles ? p.net_player_id : playerIds[0];
      add(netPlayer, 'NetPts');
      if (p.won_by === 'us') add(netPlayer, 'NetPtsWon');
    }

    // Rally length — team-level, written to both doubles partners.
    if (p.rally_bucket) {
      const key = RALLY_KEY[p.rally_bucket];
      for (const id of playerIds) {
        add(id, key);
        if (p.won_by === 'us') add(id, `${key}Won`);
      }
    }

    // Break points — regular games only, not tiebreaks.
    if (!p.is_tiebreak) {
      const key = `${p.set_index}:${p.game_index}`;
      if (key !== gameKey) {
        gameKey = key;
        gameTally = { ours: 0, theirs: 0 };
      }
      const returnerSide: Side = p.server === 'us' ? 'them' : 'us';
      const hypothetical =
        returnerSide === 'us'
          ? { ours: gameTally.ours + 1, theirs: gameTally.theirs }
          : { ours: gameTally.ours, theirs: gameTally.theirs + 1 };
      if (isGameWon(hypothetical, format.noAd) === returnerSide) {
        if (p.server === 'us') {
          add(p.server_player_id, 'BreakPtsFaced');
          if (p.won_by === 'us') add(p.server_player_id, 'BreakPtsSaved');
        } else {
          const returner = backPlayerId(p.net_player_id);
          add(returner, 'BreakPtsPlayed');
          if (p.won_by === 'us') add(returner, 'BreakPtsWon');
        }
      }
      if (p.won_by === 'us') gameTally.ours++;
      else gameTally.theirs++;
    }
  });

  // ---- Game-boundary pass: SvcGames/SvcGamesHeld/RetGames/RetGamesWon ----
  // A non-tiebreak game concludes on the point right before (set_index, game_index, is_tiebreak)
  // changes for the next point — that concluding point's won_by is exactly the game's winner.
  // The trailing group (end of the array) only counts if the match actually finished on it.
  points.forEach((p, i) => {
    if (p.is_tiebreak) return;
    const next = points[i + 1];
    const groupEnds = !next || next.set_index !== p.set_index || next.game_index !== p.game_index || next.is_tiebreak;
    if (!groupEnds) return;
    const isTrailing = i === points.length - 1;
    if (isTrailing && !engineState.winner) return; // in-progress game — not decided yet

    if (p.server === 'us') {
      add(p.server_player_id, 'SvcGames');
      if (p.won_by === 'us') add(p.server_player_id, 'SvcGamesHeld');
    } else {
      const returner = backPlayerId(p.net_player_id);
      add(returner, 'RetGames');
      if (p.won_by === 'us') add(returner, 'RetGamesWon');
    }
  });

  // ---- Team-level outcome stats, identical for every participant ----
  const ourSetsWon = engineState.sets.filter((s) => s.ourGames > s.theirGames).length;
  const ourSetsLost = engineState.sets.length - ourSetsWon;
  let ourGamesWon = engineState.sets.reduce((sum, s) => sum + s.ourGames, 0);
  let ourGamesLost = engineState.sets.reduce((sum, s) => sum + s.theirGames, 0);
  if (engineState.currentSet) {
    ourGamesWon += engineState.currentSet.ourGames;
    ourGamesLost += engineState.currentSet.theirGames;
  }
  const tiebreaksPlayed = engineState.sets.filter((s) => s.tiebreak).length;
  const tiebreaksWon = engineState.sets.filter((s) => s.tiebreak && s.ourGames > s.theirGames).length;

  for (const id of playerIds) {
    stats[id].SetsWon = ourSetsWon;
    stats[id].SetsLost = ourSetsLost;
    stats[id].GamesWon = ourGamesWon;
    stats[id].GamesLost = ourGamesLost;
    stats[id].MatchWon = engineState.winner === 'us' ? 1 : 0;
    stats[id].TiebreaksWon = tiebreaksWon;
    stats[id].TiebreaksPlayed = tiebreaksPlayed;
    // Legacy key (kept per sc-336) mirrors the new break-point funnel's terminal value.
    stats[id].BreakPointsWon = stats[id].BreakPtsWon;
  }

  return stats;
}
